<script lang="ts">
	import type { KeepExploringCategory } from '$lib/api/velatlas/response.types';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_ICON_MAP, CITY_FEATURE_TYPE_LABELS } from '$lib/features/cityFeature/cityFeatureType.enum';
	import { type NavLink, UrlUtil } from '$lib/utils/url.util';
	import type { Component } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconMapSearch from '~icons/mdi/map-search';

	const {
		location,
		categories,
		sectionClass
	}: {
		location: PlaceLocation;
		categories: KeepExploringCategory[];
		sectionClass?: ClassValue;
	} = $props();
</script>

{#if categories.length > 0}
	{const cityNav = $derived<NavLink>(UrlUtil.toCity({ location }))}
	<section class="flex flex-col gap-8 pt-8 {sectionClass}">
		<div class=" flex flex-col items-center justify-center gap-3 text-center">
			<div class="badge gap-1.5 badge-soft py-3 text-xs font-medium tracking-wide badge-secondary">
				<IconMapSearch />
				KEEP EXPLORING
			</div>

			<h2 class="text-3xl md:text-5xl">
				More to see in
				<a
					href="/{cityNav.href}"
					class="link link-primary"
					data-sveltekit-preload-data={cityNav.preloadData}
					data-sveltekit-preload-code={cityNav.preloadCode}
				>
					{location.city.label}
				</a>
			</h2>
		</div>

		<div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 lg:gap-4">
			{#each categories as category (category.type)}
				<a
					class="group relative block aspect-video w-full overflow-hidden rounded-box bg-base-200 shadow-md transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl md:aspect-16/10 xl:aspect-video"
					href={`/${category.href}`}
					title="View {CITY_FEATURE_TYPE_LABELS[category.type].toLowerCase()} in {location.city.label}"
				>
					{@render BackgroundImage(category)}
					{@render Overlay()}
					<div class="absolute inset-0 flex flex-col justify-between p-6 text-white">
						{@render Content(category)}
					</div>
				</a>
			{/each}
		</div>
	</section>
{/if}

{#snippet BackgroundImage(category: KeepExploringCategory)}
	{#if category.image}
		<img
			class="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
			src={UrlUtil.getFullImageUrl(category.image)}
			alt={`Explore ${category.title} in ${location.city.label}`}
			width={category.image.width}
			height={category.image.height}
			loading="lazy"
		/>
	{/if}
{/snippet}

{#snippet Overlay()}
	<div
		class="absolute inset-0 bg-linear-to-t from-black/90 via-black/40 to-black/20 transition-opacity duration-500 group-hover:opacity-90"
	></div>
{/snippet}

{#snippet Content(item: KeepExploringCategory)}
	{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[item.type])}

	<div class="flex items-center justify-between">
		<div class="flex size-10 items-center justify-center rounded-full border border-white/33 bg-white/20 backdrop-blur-md">
			<Icon class="text-lg" />
		</div>
	</div>

	<div class="flex items-end justify-between">
		<h3 class="max-w-[80%] text-lg">
			{item.title}
		</h3>
		<div
			class="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-content shadow-lg transition-transform duration-300 group-hover:scale-110"
		>
			<IconArrowRight class="text-lg transition-transform duration-300 group-hover:scale-110" />
		</div>
	</div>
{/snippet}
